/**
 * Canonical dispute method / family normalization for Factual Dispute wizard.
 * No structural fallback for unresolved or collection items.
 */

import {
  assembleStatusRatingText,
  hasTrueCollectionRecoveryEvidence,
  isMetro2ChargeOffRating,
} from './paymentHistoryUtils';

const COLLECTION_METHODS = new Set([
  'debt_validation',
  'collection_validation',
  'recovery_verification',
  'recovery',
  'recovery_method',
  'collection',
]);

const STRUCTURAL_METHODS = new Set([
  'structural_method',
  'round1_structural',
  'round2_structural',
  'round3_structural_prelitigation',
  'structural',
]);

const ISOLATED_METHODS = new Set([
  'isolated_method',
  'isolated',
  'round1_isolated_late_payment_factual_dispute',
  'round2_isolated_permissible_purpose_enforcement',
  'round3_isolated_late_payment_prelitigation',
  'factual_inaccuracy',
]);

const DATA_BREACH_METHODS = new Set([
  'data_breach',
  'data_breach_method',
]);

const COLLECTION_FAMILIES = new Set(['collection', 'recovery']);
const STRUCTURAL_FAMILIES = new Set(['structural']);
const ISOLATED_FAMILIES = new Set(['isolated', 'late_payment']);
const DATA_BREACH_FAMILIES = new Set(['data_breach', 'inquiry', 'personal_info']);

function firstDefined(...values) {
  for (const value of values) {
    if (value !== undefined && value !== null && value !== '') {
      return value;
    }
  }
  return null;
}

export function getStableDisputeItemId(item = {}) {
  return String(
    firstDefined(
      item.id,
      item.item_id,
      item.itemId,
      item.tradeline_id,
      item.tradelineId,
      item.inquiry_id,
      item.inquiryId,
      item.pi_id,
      item.piId
    ) || ''
  );
}

export function normalizeMethodKey(raw) {
  const method = String(raw || '').trim().toLowerCase();
  if (!method) return 'unresolved';
  if (COLLECTION_METHODS.has(method)) return 'debt_validation';
  if (STRUCTURAL_METHODS.has(method)) return 'structural_method';
  if (ISOLATED_METHODS.has(method)) return 'isolated_method';
  if (DATA_BREACH_METHODS.has(method) || method.includes('data_breach')) {
    return 'data_breach_method';
  }
  return method;
}

function looksLikeCollectionType(value) {
  const text = String(value || '').trim().toLowerCase();
  if (!text) return false;
  // Metro2 rating phrases like "Collection Or Charge Off" are charge-off ratings.
  if (
    (text.includes('charge') && text.includes('off'))
    || text.replace(/[\s_-]+/g, '').includes('chargeoff')
  ) {
    return false;
  }
  return (
    text === 'collection'
    || text === 'collections'
    || text.includes('collection account')
    || text.includes('collection agency')
  );
}

function hasCollectorRelationship(item = {}) {
  const collectionAgency = firstDefined(
    item.collection_agency,
    item.collectionAgency,
    item.collector,
    item.collector_name,
    item.collectorName
  );
  if (collectionAgency) return true;

  const originalCreditor = firstDefined(
    item.original_creditor,
    item.originalCreditor,
    item.original_creditor_name,
    item.originalCreditorName
  );
  if (!originalCreditor) return false;

  const furnisher = String(
    firstDefined(
      item.creditor_name,
      item.creditorName,
      item.account_name,
      item.accountName,
      item.furnisher
    ) || ''
  ).trim().toLowerCase();
  const original = String(originalCreditor).trim().toLowerCase();
  if (!furnisher) return true;
  return furnisher !== original;
}

/**
 * Canonical normalizer.
 * Precedence:
 * 1) Explicit resolved backend method (debt_validation / recovery_*)
 * 2) Explicit dispute family (collection / recovery)
 * 3) Explicit is_collection boolean
 * 4) Account type detail Collection / Collections / Collection Account
 * 5) Collector / original-creditor relationship
 *
 * Never falls back to structural for unresolved items.
 */
export function normalizeDisputeMethod(item = {}, resolvedRule = null) {
  const resolvedMethod = normalizeMethodKey(
    firstDefined(
      resolvedRule?.assigned_method,
      resolvedRule?.method,
      resolvedRule?.resolved_method,
      resolvedRule?.resolvedMethod,
      item.resolved_method,
      item.resolvedMethod,
      item.method,
      item.assigned_method,
      item.method_assignment?.assigned_method
    )
  );

  // 1) Explicit collection/recovery methods win
  if (COLLECTION_METHODS.has(resolvedMethod) || resolvedMethod === 'debt_validation') {
    return {
      disputeFamily: 'collection',
      method: 'debt_validation',
      isCollection: true,
    };
  }

  const familyRaw = String(
    firstDefined(
      resolvedRule?.dispute_family,
      resolvedRule?.disputeFamily,
      item.dispute_family,
      item.disputeFamily,
      item.family
    ) || ''
  ).trim().toLowerCase();

  // 2) Explicit collection/recovery family
  if (COLLECTION_FAMILIES.has(familyRaw)) {
    return {
      disputeFamily: 'collection',
      method: 'debt_validation',
      isCollection: true,
    };
  }

  // 3) Explicit boolean — overrides stale structural method labels,
  //    but Metro2 rating-only pollution must not invent true collection.
  const isCollectionFlag = Boolean(
    firstDefined(item.is_collection, item.isCollection, resolvedRule?.is_collection, resolvedRule?.isCollection)
  );
  // Concatenate every status/rating field — never short-circuit with `a || b`.
  const ratingBlob = assembleStatusRatingText(item);
  const metro2ChargeOffRating = isMetro2ChargeOffRating(ratingBlob);
  const trueRecovery = hasTrueCollectionRecoveryEvidence(item);
  if (trueRecovery || (isCollectionFlag && !metro2ChargeOffRating)) {
    return {
      disputeFamily: 'collection',
      method: 'debt_validation',
      isCollection: true,
    };
  }

  // 4) Account type detail
  const accountTypeDetail = firstDefined(
    item.account_type_detail,
    item.accountTypeDetail,
    item.account_type,
    item.accountType,
    resolvedRule?.account_type_detail
  );
  if (looksLikeCollectionType(accountTypeDetail)) {
    return {
      disputeFamily: 'collection',
      method: 'debt_validation',
      isCollection: true,
    };
  }

  // 5) Collector / original-creditor relationship
  if (hasCollectorRelationship(item)) {
    return {
      disputeFamily: 'collection',
      method: 'debt_validation',
      isCollection: true,
    };
  }

  if (STRUCTURAL_METHODS.has(resolvedMethod) || STRUCTURAL_FAMILIES.has(familyRaw)) {
    return {
      disputeFamily: 'structural',
      method: 'structural_method',
      isCollection: false,
    };
  }

  if (ISOLATED_METHODS.has(resolvedMethod) || ISOLATED_FAMILIES.has(familyRaw)) {
    return {
      disputeFamily: 'isolated',
      method: 'isolated_method',
      isCollection: false,
    };
  }

  if (
    DATA_BREACH_METHODS.has(resolvedMethod)
    || DATA_BREACH_FAMILIES.has(familyRaw)
    || String(resolvedMethod).includes('data_breach')
  ) {
    return {
      disputeFamily: 'data_breach',
      method: 'data_breach_method',
      isCollection: false,
    };
  }

  if (!resolvedMethod || resolvedMethod === 'unresolved' || resolvedMethod === 'unassigned') {
    return {
      disputeFamily: 'unresolved',
      method: 'unresolved',
      isCollection: false,
    };
  }

  return {
    disputeFamily: 'unresolved',
    method: 'unresolved',
    isCollection: false,
  };
}

export function getCanonicalPackageLabel(methodOrFamily) {
  const key = normalizeMethodKey(methodOrFamily);
  if (key === 'isolated_method') return 'Isolated Strategy';
  if (key === 'structural_method') return 'Structural Strategy';
  if (key === 'debt_validation') return 'Debt Validation / Recovery';
  if (key === 'data_breach_method') return 'Data Breach Method';
  if (key === 'unresolved') return 'Unresolved';
  return 'Unresolved';
}

export function buildResolveMethodsCacheKey({
  round = 1,
  mode = 'factual',
  items = [],
} = {}) {
  const normalizedItems = (items || [])
    .map((item) => ({
      id: getStableDisputeItemId(item),
      disputeFamily:
        item.dispute_family
        || item.disputeFamily
        || null,
      method:
        item.method
        || item.resolved_method
        || item.resolvedMethod
        || null,
      accountType:
        item.account_type_detail
        || item.accountTypeDetail
        || null,
      isCollection: Boolean(item.is_collection ?? item.isCollection),
      isChargeoff: Boolean(item.is_chargeoff ?? item.isChargeoff),
      originalCreditor:
        item.original_creditor
        || item.originalCreditor
        || null,
    }))
    .sort((a, b) => String(a.id).localeCompare(String(b.id)));

  return JSON.stringify({
    round,
    mode,
    items: normalizedItems,
  });
}

/**
 * Detect collection signals from a raw account object for resolve-methods payload.
 */
export function detectCollectionSignals(account = {}) {
  const normalized = normalizeDisputeMethod(account);
  if (normalized.isCollection) {
    return {
      is_collection: true,
      account_type_detail:
        account.account_type_detail
        || account.accountTypeDetail
        || 'Collection',
      original_creditor:
        account.original_creditor
        || account.originalCreditor
        || null,
    };
  }

  const bureauComparisonTypeDetail = account.bureau_comparison?.account_type_detail || {};
  const fromBureau = Object.values(bureauComparisonTypeDetail).some((val) =>
    looksLikeCollectionType(val)
  );
  const fromBureauValues = Object.values(account.bureau_values || {}).some((val) =>
    looksLikeCollectionType(val?.account_type_detail)
    || looksLikeCollectionType(val?.account_type)
  );

  return {
    is_collection: fromBureau || fromBureauValues,
    account_type_detail:
      account.account_type_detail
      || account.accountTypeDetail
      || Object.values(bureauComparisonTypeDetail).find(Boolean)
      || '',
    original_creditor:
      account.original_creditor
      || account.originalCreditor
      || null,
  };
}
